import { Shield } from "lucide-react";
import { useState, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/DefaultSelectDropdown";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import ContextMenu, { ContextMenuItem } from "@/components/ui/ContextMenu";
import { getCompanyRoles, getActualUserRole } from "@/lib/utils/permissions";
import { useDevRoleOverride, isDevelopmentMode } from "@/lib/utils/devRoleOverride";
import { CompanyRole } from "@/lib/stores/storeTypes";

interface RoleSwitcherProps {
  collapsed?: boolean;
}

export default function RoleSwitcher({ collapsed = false }: RoleSwitcherProps) {
  // Don't render if not in development mode
  if (!isDevelopmentMode()) {
    return null;
  }

  const { selectedCompany } = useSimplifiedUserData();
  const { overrideRoleId, setOverrideRole, clearOverride } = useDevRoleOverride();
  const [roles, setRoles] = useState<CompanyRole[]>([]);
  const [contextMenu, setContextMenu] = useState<{ isOpen: boolean; position: { x: number; y: number } }>({
    isOpen: false,
    position: { x: 0, y: 0 },
  });

  // Load roles when company changes
  useEffect(() => {
    if (selectedCompany) {
      const companyRoles = getCompanyRoles(selectedCompany.id);
      setRoles(companyRoles);
    }
  }, [selectedCompany]);

  if (!selectedCompany || roles.length === 0) {
    return null;
  }

  // Get actual user role (ignores any override)
  const actualUserRole = getActualUserRole(selectedCompany.id);
  const currentRoleId = overrideRoleId || 'actual-role';

  const handleRoleChange = (roleId: string) => {
    const role = roles.find(r => r.id === roleId);
    const roleName = role?.role_name || 'Actual Role';
    
    if (roleId === 'actual-role') {
      console.log('🎭 [RoleSwitcher] Clearing role override - returning to actual role');
      clearOverride();
    } else {
      console.log('🎭 [RoleSwitcher] Switching to role:', roleName, '(ID:', roleId, ')');
      console.log('🎭 [RoleSwitcher] ⚠️ All components will now re-check permissions for this role');
      setOverrideRole(roleId);
    }
  };

  if (collapsed) {
    const menuItems: ContextMenuItem[] = [
      {
        id: 'actual-role',
        label: `${actualUserRole} (Actual)`,
        onClick: () => handleRoleChange('actual-role'),
        disabled: currentRoleId === 'actual-role',
      },
      ...roles.map((role) => ({
        id: role.id,
        label: `${role.role_name}${overrideRoleId === role.id ? ' ✓' : ''}`,
        onClick: () => handleRoleChange(role.id),
        disabled: overrideRoleId === role.id,
      })),
    ];

    return (
      <>
        <button
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setContextMenu({
              isOpen: true,
              position: {
                x: rect.right + 8,
                y: rect.top,
              },
            });
          }}
          className="flex items-center justify-center w-8 h-8 rounded-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
          style={{ 
            backgroundColor: overrideRoleId ? 'rgba(255, 165, 0, 0.2)' : undefined,
            border: overrideRoleId ? '1px solid rgba(255, 165, 0, 0.5)' : undefined
          }}
          title={`Dev Role Override\nCurrent: ${overrideRoleId ? roles.find(r => r.id === overrideRoleId)?.role_name : actualUserRole}\nClick to select role`}
        >
          <Shield className="h-4 w-4 flex-shrink-0" style={{ color: overrideRoleId ? 'orange' : undefined }} />
        </button>
        <ContextMenu
          isOpen={contextMenu.isOpen}
          position={contextMenu.position}
          items={menuItems}
          onClose={() => setContextMenu({ ...contextMenu, isOpen: false })}
        />
      </>
    );
  }

  return (
    <div className="px-2 py-2">
      <Select
        value={currentRoleId}
        onValueChange={handleRoleChange}
      >
        <SelectTrigger 
          className="w-full"
          style={{ 
            backgroundColor: overrideRoleId ? 'rgba(255, 165, 0, 0.1)' : undefined,
            border: overrideRoleId ? '1px solid rgba(255, 165, 0, 0.5)' : undefined
          }}
        >
          <div className="flex items-center gap-2 w-full">
            <Shield className="h-4 w-4 flex-shrink-0" style={{ color: overrideRoleId ? 'orange' : undefined }} />
            <SelectValue placeholder="Select Role (Dev)" />
          </div>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="actual-role">
            {actualUserRole} (Actual)
          </SelectItem>
          {roles.map((role) => (
            <SelectItem key={role.id} value={role.id}>
              {role.role_name} {role.is_built_in ? '(Built-in)' : '(Custom)'}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {overrideRoleId && (
        <div className="px-2 py-1 text-xs text-orange-600 dark:text-orange-400">
          🎭 Dev Override Active
        </div>
      )}
    </div>
  );
}

